/* eslint-disable @typescript-eslint/no-explicit-any */
import { getMe, getMeFirstLoad } from '@portal/actions/user';
import { appCheckFlag, loginFlags } from '@portal/config/app.json';
import { KEYCLOAK_LOGIN_ENABLED } from '@portal/config/env';
import { EventType } from '@portal/enum/analytics';
import { TokenMethod } from '@portal/enum/device';
import ErrorsMessages from '@portal/enum/errors';
import { UserCredentialType } from '@portal/models/enumerators/auth';
import { TabKeys } from '@portal/pages/Auth/PasswordRecovery/PasswordRecovery';
import {
  encrypt,
  removeHeader,
  setHeader,
} from '@portal/repositories/securitySdkConfig';
import { appCheck, sendEvent } from '@portal/services/firebase';
import { isCNPJ, isCpf, unmaskField } from '@portal/services/masks';
import { setHeaderDevice } from '@portal/utils/device';
import * as Sentry from '@sentry/react';
import { getToken } from 'firebase/app-check';
import AuthRequests from '~/repositories/auth';
import * as MessageService from '~/services/message';
import centralNavigationService from '~/services/navigation';
import * as StorageService from '~/services/storage';
import {
  AUTH_CHANGE_USER_CREDENTIAL,
  AUTH_CHANGE_USER_CREDENTIAL_ERROR,
  AUTH_FORGOT_LOGIN,
  AUTH_LOGIN,
  AUTH_LOGOUT,
  AUTH_UPDATE_USER_TOKEN,
  CLEAN_CREDENTIALS_ERROR,
  SET_CREDENTIALS_ERROR,
  WARN_LOGIN_ATTEMPTS_ERROR,
} from './actionTypes';
import { decreaseLoading, increaseLoading } from './loading';

export const authenticate =
  (userData: models.AuthRequest) => async (dispatch: any) => {
    dispatch(increaseLoading());
    removeHeader(StorageService.StorageItems.PAYMENT_ACCOUNT_ID, 'session');
    try {
      const deviceId = StorageService.getOrCreateDeviceId();

      const payload = await Sentry.startSpan(
        {
          op: 'auth.login',
          name: 'Login',
        },
        () => AuthRequests.login(userData)
      );

      StorageService.setItem('session-token', JSON.stringify(payload));
      StorageService.setItem(
        StorageService.StorageItems.MAIN_ACCOUNT_ACCESS_TOKEN,
        JSON.stringify(payload)
      );

      const loginsAttemptsErrorsString = StorageService.getItem(
        'logins-attempts-errors'
      );
      if (loginsAttemptsErrorsString) {
        const loginsAttemptsErrorsObj = JSON.parse(loginsAttemptsErrorsString);
        const loginsAttemptsErrorsNewValue = {
          ...loginsAttemptsErrorsObj,
          [userData.username]: 0,
        };
        StorageService.setItem(
          'logins-attempts-errors',
          JSON.stringify(loginsAttemptsErrorsNewValue)
        );
      }

      if (appCheckFlag?.enabled && appCheck) {
        const { token: tokenAppCheck } = await getToken(appCheck, true);

        setHeader(
          StorageService.StorageItems.FIREBASE_APP_CHECK,
          tokenAppCheck,
          'local'
        );
      }

      setHeaderDevice(deviceId);

      setHeader(
        StorageService.StorageItems.AUTHORIZATION,
        `Bearer ${payload.accessToken}`
      );

      setHeader(StorageService.StorageItems.DEVICE_ID, deviceId, 'local');

      if (loginFlags?.customCredentialsErrorFlow) {
        dispatch(cleanCredentialsError());
      }

      dispatch(getMeFirstLoad());

      dispatch({
        payload,
        type: AUTH_LOGIN,
      });
      Sentry.logger.info('Login successful', { username: userData.username });
    } catch (err: any) {
      Sentry.logger.error('Login failed', {
        username: userData.username,
        status: err?.response?.status,
        error: err?.response?.data?.error,
      });
      StorageService.removeItem('session-token');
      StorageService.clearLocalStorage();

      if (userData.username) {
        const loginsAttemptsErrors = JSON.parse(
          StorageService.getItem('logins-attempts-errors') || '{}'
        );

        const userAttempts = loginsAttemptsErrors[userData.username];
        const loginsAttemptsErrorsNewValue = {
          ...loginsAttemptsErrors,
          [userData.username]: userAttempts ? userAttempts + 1 : 1,
        };

        StorageService.setItem(
          'logins-attempts-errors',
          JSON.stringify(loginsAttemptsErrorsNewValue)
        );

        const maxAttemptsToShowWarningModal = 2;

        if (
          loginsAttemptsErrorsNewValue[userData.username] ===
          maxAttemptsToShowWarningModal
        ) {
          dispatch({
            payload: true,
            type: WARN_LOGIN_ATTEMPTS_ERROR,
          });
        }
      }

      if (
        err?.response?.status === 400 &&
        /Account disabled/.test(err?.response?.data?.error_description)
      ) {
        return MessageService.error(ErrorsMessages.DISABLED_USER);
      }

      if (
        loginFlags?.customCredentialsErrorFlow?.enabled &&
        loginFlags?.customCredentialsErrorFlow?.maxLoginAttempts &&
        /Invalid user credentials/.test(err?.response?.data?.error_description)
      ) {
        const { credentialsError = 0 } = userData;
        dispatch(setCredentialsError());

        if (
          credentialsError ===
          loginFlags?.customCredentialsErrorFlow?.maxLoginAttempts - 1
        ) {
          return MessageService.error(ErrorsMessages.NEXT_TO_LAST_ATTEMPT);
        }

        if (
          credentialsError >=
          loginFlags?.customCredentialsErrorFlow?.maxLoginAttempts
        ) {
          return MessageService.error(ErrorsMessages.LAST_ATTEMPT);
        }

        return MessageService.error(ErrorsMessages.INVALID_CREDENTIALS);
      }
      if (err?.response?.data?.error === ErrorsMessages.INVALID_GRANT) {
        if (/Account disabled/.test(err?.response?.data?.error_description)) {
          return MessageService.error(ErrorsMessages.DISABLED_USER);
        } else {
          return MessageService.error(ErrorsMessages.INVALID_CREDENTIALS);
        }
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const keycloackAuthenticate =
  (payload: models.AuthResponse) => async (dispatch: any) => {
    dispatch(increaseLoading());
    try {
      const deviceId = StorageService.getOrCreateDeviceId();

      StorageService.setItem('session-token', JSON.stringify(payload));

      setHeader(
        StorageService.StorageItems.AUTHORIZATION,
        `Bearer ${payload.accessToken}`
      );
      setHeaderDevice(deviceId);

      if (window.location.pathname === '/') {
        dispatch(getMeFirstLoad());
      }

      dispatch({
        payload,
        type: AUTH_LOGIN,
      });
      Sentry.logger.info('Keycloak login successful');
    } catch (err) {
      Sentry.logger.error('Keycloak login failed', {
        message: err instanceof Error ? err.message : String(err),
      });
      if (
        err instanceof Error &&
        err.message === ErrorsMessages.INVALID_CREDENTIALS
      ) {
        MessageService.error(ErrorsMessages.INVALID_CREDENTIALS);
      }
      StorageService.removeItem('session-token');
      StorageService.clearLocalStorage();
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const logout = () => async (dispatch) => {
  dispatch(increaseLoading());
  sendEvent(EventType.LOGOUT);
  Sentry.logger.info('Logout initiated');

  try {
    StorageService.removeItem('session-token');
    StorageService.removeItem(
      StorageService.StorageItems.MAIN_ACCOUNT_ACCESS_TOKEN
    );
    StorageService.removeItem(
      StorageService.StorageItems.MAIN_ACCOUNT_REFRESH_TOKEN
    );
    StorageService.removeItem(
      StorageService.StorageItems.PIX_PORTABILITY_CONFIRMED
    );

    if (!KEYCLOAK_LOGIN_ENABLED) {
      window.location.href = '/';
      return;
    }

    dispatch({
      type: AUTH_LOGOUT,
    });
  } catch (err) {
    Sentry.logger.error('Logout failed', {
      message: err instanceof Error ? err.message : String(err),
    });
    MessageService.error('APPLICATION.ERRORS.GENERIC');
  } finally {
    dispatch(decreaseLoading());
  }
};

export const logoutCleanRedux = () => async (dispatch) => {
  dispatch({
    type: AUTH_LOGOUT,
  });
  StorageService.removeItem('session-token');
  StorageService.removeItem(
    StorageService.StorageItems.MAIN_ACCOUNT_ACCESS_TOKEN
  );
  StorageService.removeItem(
    StorageService.StorageItems.MAIN_ACCOUNT_REFRESH_TOKEN
  );
};

export const validateSsoToken = (ssoToken: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const { accessToken, refreshToken } = await AuthRequests.validateSsoToken(
      ssoToken
    );

    if (accessToken && refreshToken) {
      dispatch(checkIsLogged(accessToken));
    }
  } catch (err) {
    StorageService.removeItem('session-token');
    StorageService.clearLocalStorage();
  } finally {
    dispatch(decreaseLoading());
  }
};

export const checkIsLogged =
  (accessToken?: string, refreshToken?: string) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const token = StorageService.getItem('session-token');

      if (accessToken) {
        setHeader(
          StorageService.StorageItems.AUTHORIZATION,
          `Bearer ${accessToken}`
        );

        const payload = {
          accessToken,
          refreshToken,
        };
        StorageService.setItem('session-token', JSON.stringify(payload));

        const deviceId = StorageService.getOrCreateDeviceId();
        setHeaderDevice(deviceId);

        dispatch({
          payload,
          type: AUTH_LOGIN,
        });

        dispatch(getMe());
      }

      let tokens: models.AuthResponse | null = null;
      if (token) {
        try {
          tokens = JSON.parse(token);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(
            'Failed to parse session token, clearing invalid token:',
            error
          );
          StorageService.removeItem('session-token');
          return;
        }
      }
      if (tokens && tokens.accessToken && !accessToken) {
        setHeader(
          StorageService.StorageItems.AUTHORIZATION,
          `Bearer ${tokens.accessToken}`
        );
        dispatch({
          payload: {
            ...tokens,
          },
          type: AUTH_LOGIN,
        });

        dispatch(getMe());
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const recoveryPassword =
  (
    {
      taxIdentifier,
      userLogin,
      method,
    }: {
      taxIdentifier: string;
      userLogin: string;
      method?: TokenMethod;
    },
    setCurrentTab: (tab: string) => void
  ) =>
  async (dispatch) => {
    dispatch(increaseLoading());
    sendEvent(EventType.FORGOT_PASSWORD);
    try {
      const cleanLogin =
        isCpf(userLogin) || isCNPJ(userLogin)
          ? unmaskField(userLogin)
          : userLogin;

      await AuthRequests.recoveryPassword(
        taxIdentifier,
        cleanLogin,
        method || TokenMethod.SMS
      );

      setCurrentTab(TabKeys.SMS);
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const forgotLogin = (taxIdentifier: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await AuthRequests.forgotLogin(taxIdentifier);
    dispatch({
      payload: 'recovery',
      type: AUTH_FORGOT_LOGIN,
    });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch({
      payload: '',
      type: AUTH_FORGOT_LOGIN,
    });
    dispatch(decreaseLoading());
  }
};

export const changePassword =
  (body: models.ChangePasswordRequest) => async (dispatch) => {
    dispatch(increaseLoading());

    try {
      await AuthRequests.changePassword(body);

      MessageService.success('PAGES.AUTH.RECOVER.TABS.CREDENTIALS.SUCCESS');
      centralNavigationService.navigate('/');
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const userCredentialUpdateToken =
  (method?: TokenMethod) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await AuthRequests.userCredentialUpdateToken(method);

      dispatch({ payload: true, type: AUTH_UPDATE_USER_TOKEN });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch({ payload: false, type: AUTH_UPDATE_USER_TOKEN });
      dispatch(decreaseLoading());
    }
  };

export const userCredentialChangeCredential =
  (body: models.UpdateUserCredentialRequest) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const encryptedToken = await encrypt(body.token);
      const encryptedCredential = await encrypt(body.credential);

      const requestBody = {
        ...body,
        token: encryptedToken,
        credential: encryptedCredential,
      };

      await AuthRequests.userCredentialChangeCredential(requestBody);
      if (body.type === UserCredentialType.PASSWORD) {
        MessageService.success(
          'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.SUCCESS'
        );
        sendEvent(EventType.CHANGE_PASSWORD);
      } else {
        MessageService.success('PAGES.AUTH.CHANGE_CREDENTIALS.PIN.SUCCESS');
        sendEvent(EventType.CHANGE_PIN);
      }
      centralNavigationService.navigate('/');
      dispatch(userCredentialSuccess(true));
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
        dispatch(userCredentialError(err));
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const userCredentialSuccess = (payload: boolean) => ({
  payload,
  type: AUTH_CHANGE_USER_CREDENTIAL,
});

export const userCredentialError = (payload: Error | null) => ({
  payload,
  type: AUTH_CHANGE_USER_CREDENTIAL_ERROR,
});

export const setCredentialsError = () => ({
  type: SET_CREDENTIALS_ERROR,
});

export const cleanCredentialsError = () => ({
  type: CLEAN_CREDENTIALS_ERROR,
});
